import { api } from '../../app/api/baseApi';
import { PAGINATION_PAYLOAD } from '../../constants/paginationQuery';
import { environmentalStressMonitoringPreviewResponse } from './environmentalStressMonitoringPreview.mock';

/**
 * Arguments accepted by the `updatePredictionStatus` mutation.
 *
 * `status` is deliberately a plain string rather than `UpcomingSelectableStatus`:
 * the event-detail screens (ServiceFans, RenewServiceContract,
 * BatteryDegradationDetected) still pass mixed-case values such as 'Open'.
 */
export type UpdatePredictionStatusArgs = {
    upcomingId: string;
    body: { status: string };
    /** Skip the global error toast so the caller can surface its own message. */
    suppressErrorToast?: boolean;
};

export type PredictionDownloadArgs = {
    body: {
        page: number;
        size: number;
        sort?: { key: string; sortType: string };
        filters: Record<string, unknown>;
    };
};

export type PredictionDownloadResponse = {
    data: string;
};

export const predictionApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getPredictionFilters: builder.query<any, any>({
            query: (args: any = {}) => {
                const safeArgs = args && typeof args === 'object' ? args : {};
                const { body, realm, organizationId } = safeArgs;
                const bodyClone = body && typeof body === 'object' ? { ...body } : {};
                return {
                    url: '/predictions/v1/prediction/filter',
                    method: 'POST',
                    body: bodyClone,
                    headers: {
                        ...(realm ? { 'x-realm': realm } : {}),
                        ...(organizationId ? { organizationId: organizationId } : {}),
                    },
                };
            },
            providesTags: ['UpcomingList'],
        }),
        getEnvironmentalStressMonitoringPreview: builder.query<any, void>({
            queryFn: () => ({
                data: environmentalStressMonitoringPreviewResponse,
            }),
            keepUnusedDataFor: 0,
        }),
        getEnvironmentalStressMonitoringSummary: builder.query<any, void>({
            queryFn: () => ({
                data: {
                    data: {
                        overview: environmentalStressMonitoringPreviewResponse.data.overview,
                    },
                },
            }),
            keepUnusedDataFor: 0,
        }),
        getMobilePredictions: builder.query<any, any>({
            query: (body) => {
                const payloadClone = JSON.parse(JSON.stringify(body ?? {}));
                const uniqueIdentifiers = Array.isArray(payloadClone?.uniqueIdentifiers)
                    ? payloadClone.uniqueIdentifiers
                    : [];
                return {
                    url: '/predictions/v1/prediction/summary',
                    method: 'POST',
                    body: {
                        uniqueIdentifiers,
                    },
                };
            },
            ...PAGINATION_PAYLOAD,
        }),

        getPredictionById: builder.query<any, any>({
            query: (predictionId) => ({
                url: `/predictions/v1/prediction/${predictionId}`,
                method: 'GET',
            }),
            providesTags: ['UpcomingById'],
        }),
        getCapacitorRulDetailById: builder.query<any, any>({
            query: (predictionId) => ({
                url: `/predictions/v1/prediction/${predictionId}/capacitor_rul/detail`,
                method: 'GET',
            }),
            providesTags: ['UpcomingById'],
        }),
        getPredictionSummary: builder.query<any, any>({
            query: (payload) => {
                const requestPayload = payload && payload.body ? payload.body : payload;
                const bodyClone = JSON.parse(JSON.stringify(requestPayload ?? {}));

                return {
                    url: '/predictions/v1/prediction/summary',
                    method: 'POST',
                    body: {
                        uniqueIdentifiers: Array.isArray(bodyClone?.uniqueIdentifiers)
                            ? bodyClone.uniqueIdentifiers
                            : [],
                    },
                    headers: {
                        ...(payload?.realm ? { 'x-realm': payload.realm } : {}),
                        ...(payload?.organizationId ? { organizationId: payload.organizationId } : {}),
                    },
                };
            },
        }),
        downloadPrediction: builder.mutation<PredictionDownloadResponse, PredictionDownloadArgs>({
            query: ({ body }: PredictionDownloadArgs) => ({
                url: `/predictions/v1/prediction/download`,
                method: 'POST',
                body,
            }),
        }),
        updatePredictionAcknowledge: builder.mutation({
            query: ({ upcomingId, body }: any) => ({
                url: `/predictions/v1/prediction/${upcomingId}/acknowledge`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error) => (!error ? ['UpcomingById'] : []),
        }),
        createServiceRequest: builder.mutation({
            query: ({ body, realm }: { body: any; realm?: string }) => ({
                url: '/predictions/v1/user/serviceRequest',
                method: 'POST',
                body,
                headers: {
                    ...(realm ? { 'x-realm': realm } : {}),
                },
            }),
            invalidatesTags: (result, error) => (!error ? ['UpcomingById'] : []),
        }),
        getPredictionEvidence: builder.query<any, any>({
            query: ({ predictionId, type }) => ({
                url: `/predictions/v1/prediction/${predictionId}/${type}/evidence`,
                method: 'GET',
            }),
        }),
        getScoreList: builder.query<any, any>({
            query: () => ({
                url: `/ds/v1/demo/parameters`,
                method: 'GET',
            }),
        }),
        updatePredictionStatus: builder.mutation<unknown, UpdatePredictionStatusArgs>({
            // Callers that surface their own failure message can opt out of the global
            // error toast per call; baseApi reads `suppressErrorToast` from the args.
            query: ({ upcomingId, body, suppressErrorToast }: UpdatePredictionStatusArgs) => ({
                url: `/predictions/v1/prediction/${upcomingId}/status`,
                method: 'PATCH',
                body,
                suppressErrorToast,
            }),
            invalidatesTags: (result, error) => (!error ? ['UpcomingById', 'UpcomingList'] : []),
        }),
        resetPredictionLifecycle: builder.mutation({
            query: ({ upcomingId }: any) => ({
                url: `/predictions/v1/prediction/${upcomingId}/lifecycle-reset`,
                method: 'POST',
            }),
            invalidatesTags: (result, error) => (!error ? ['UpcomingById'] : []),
        }),
        updatePredictionInstallationDate: builder.mutation({
            query: ({ upcomingId, body }: any) => ({
                url: `/predictions/v1/prediction/${upcomingId}/installation-date`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error) => (!error ? ['UpcomingById'] : []),
        }),
        getCommentBySerialNumber: builder.query<any, any>({
            query: (serialNumber) => ({
                url: `/predictions/v1/admin/comment/${serialNumber}`,
                method: 'GET',
            }),
            providesTags: ['CommentBySerialNumber'],
        }),
        saveCommentBySerialNumber: builder.mutation({
            query: ({ serialNumber, body }: any) => ({
                url: `/predictions/v1/admin/comment/${serialNumber}`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error) => (!error ? ['CommentBySerialNumber'] : []),
        }),
    }),
    overrideExisting: true,
});
export const {
    useGetPredictionFiltersQuery,
    useDownloadPredictionMutation,
    useGetEnvironmentalStressMonitoringPreviewQuery,
    useGetEnvironmentalStressMonitoringSummaryQuery,
    useGetMobilePredictionsQuery,
    useGetPredictionByIdQuery,
    useGetCapacitorRulDetailByIdQuery,
    useGetPredictionSummaryQuery,
    useGetPredictionEvidenceQuery,
    useUpdatePredictionAcknowledgeMutation,
    useCreateServiceRequestMutation,
    useGetScoreListQuery,
    useUpdatePredictionStatusMutation,
    useResetPredictionLifecycleMutation,
    useUpdatePredictionInstallationDateMutation,
    useGetCommentBySerialNumberQuery,
    useSaveCommentBySerialNumberMutation,
} = predictionApi;
